import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  IndianRupee, Check, Package, FileDown, Eye, ArrowLeft, BadgeCheck, Printer, Download,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { CheckoutModal } from "@/components/CheckoutModal";
import { PreviewModal } from "@/components/PreviewModal";
import { Seo } from "@/components/Seo";
import { API, imgUrl } from "@/lib/api";

const PRODUCT_FAQ = [
  { q: "Is this a digital product?", a: "Yes. This is a printable PDF digital download. No physical item will be shipped." },
  { q: "How do I receive my PDF?", a: "After successful payment on our secure checkout, your PDF is delivered automatically — you receive it by email right away." },
  { q: "Can I print it more than once?", a: "Yes! You can print the pages as many times as you like for your own home or classroom use." },
  { q: "What ages is it for?", a: "It is designed for children aged 3 to 6 — preschool, nursery, LKG and UKG learners." },
];

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [buyOpen, setBuyOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  useEffect(() => {
    setProduct(null);
    setNotFound(false);
    axios
      .get(`${API}/products/${slug}`)
      .then((r) => setProduct(r.data))
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div data-testid="product-not-found" className="mx-auto max-w-2xl px-6 py-24 text-center">
        <h1 className="font-display text-4xl font-bold text-slate-800">Product not found</h1>
        <p className="mt-3 font-semibold text-slate-500">This product may have been removed.</p>
        <Link to="/shop" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-blue px-7 py-3 font-display text-lg font-bold text-white">
          <ArrowLeft className="h-5 w-5" /> Back to Shop
        </Link>
      </div>
    );
  }

  if (!product) {
    return <div className="mx-auto max-w-6xl px-6 py-24 text-center font-display text-xl font-bold text-slate-400">Loading...</div>;
  }

  return (
    <div data-testid="product-detail-page" className="mx-auto max-w-6xl px-6 py-12">
      <Seo
        title={`${product.name} | Divine Kids Learning`}
        description={product.description}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          description: product.description,
          brand: { "@type": "Brand", name: "Divine Kids Learning" },
          offers: {
            "@type": "Offer",
            price: String(product.price),
            priceCurrency: "INR",
            availability:
              product.available === false
                ? "https://schema.org/PreOrder"
                : "https://schema.org/InStock",
          },
        }}
      />

      <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 font-display text-base font-bold text-brand-blue hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to Shop
      </Link>

      <div className="mt-8 grid gap-12 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
          <div
            className="flex items-center justify-center overflow-hidden rounded-[2.5rem] border-8 border-white shadow-2xl shadow-blue-900/10"
            style={{ backgroundColor: `${product.accent}14` }}
          >
            <img
              src={imgUrl(product.cover_image)}
              alt={product.name}
              className="h-96 w-auto max-w-full object-contain p-6 drop-shadow-xl sm:h-[30rem]"
            />
          </div>
          {product.preview_images?.length > 0 && (
            <div className="mt-6">
              <p className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-700">
                <Eye className="h-5 w-5 text-brand-blue" /> Sample worksheet previews
              </p>
              <div className="grid grid-cols-3 gap-3">
                {product.preview_images.map((src, i) => (
                  <button
                    key={src}
                    data-testid={`preview-thumb-${i}`}
                    onClick={() => setPreviewOpen(true)}
                    className="overflow-hidden rounded-2xl border-4 border-white shadow-md transition-transform hover:-translate-y-1"
                  >
                    <img src={imgUrl(src)} alt={`${product.name} sample page ${i + 1}`} loading="lazy" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5 }}>
          <span
            className="inline-block rounded-full px-4 py-1.5 font-display text-sm font-bold text-white"
            style={{ backgroundColor: product.accent }}
          >
            {product.tagline}
          </span>
          <h1 className="mt-4 font-display text-4xl font-black tracking-tight text-slate-800 sm:text-5xl">
            {product.name}
          </h1>
          <p className="mt-4 text-lg font-semibold leading-relaxed text-slate-500">{product.description}</p>

          <div className="mt-5 flex flex-wrap gap-3">
            <span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-slate-600 shadow-sm">Ages {product.ages}</span>
            <span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-slate-600 shadow-sm">{product.pages} printable pages</span>
            <span className="rounded-full bg-white px-4 py-2 text-sm font-bold text-slate-600 shadow-sm">PDF format</span>
          </div>

          <div className="mt-8 rounded-[2rem] bg-white p-7 shadow-xl shadow-blue-900/5">
            <h2 className="font-display text-xl font-bold text-slate-800">What children will learn</h2>
            <ul className="mt-4 space-y-3">
              {product.learn.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base font-semibold text-slate-600">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100">
                    <Check className="h-4 w-4 text-brand-green" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
            <h2 className="mt-7 font-display text-xl font-bold text-slate-800">What's included</h2>
            <ul className="mt-4 space-y-3">
              {product.included.map((item) => (
                <li key={item} className="flex items-start gap-3 text-base font-semibold text-slate-600">
                  <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-sky-100">
                    <Package className="h-4 w-4 text-brand-blue" />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <p
            data-testid="detail-digital-notice"
            className="mt-6 flex items-start gap-2 rounded-2xl bg-amber-50 px-5 py-4 text-base font-semibold text-amber-700"
          >
            <FileDown className="mt-0.5 h-5 w-5 shrink-0" />
            This is a digital PDF product. No physical item will be shipped.
          </p>

          {product.available === false ? (
            <p
              data-testid="detail-coming-soon"
              className="mt-7 rounded-2xl bg-slate-100 px-6 py-5 text-center font-display text-xl font-bold text-slate-500"
            >
              Coming Soon — this workbook is being prepared
            </p>
          ) : (
            <div className="mt-7 flex items-center justify-between gap-4">
              <p className="flex items-center font-display text-5xl font-black text-slate-800">
                <IndianRupee className="h-9 w-9" />
                {product.price}
              </p>
              <button
                data-testid="detail-buy-now"
                onClick={() =>
                  product.payment_link
                    ? window.open(product.payment_link, "_blank", "noopener")
                    : setBuyOpen(true)
                }
                className="rounded-full px-10 py-5 font-display text-2xl font-bold text-white transition-transform active:translate-y-1 active:shadow-none"
                style={{ backgroundColor: product.accent, boxShadow: `0 6px 0 0 ${product.accent}99` }}
              >
                Buy Now
              </button>
            </div>
          )}

          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            {[
              { Icon: Download, label: "Secure download" },
              { Icon: Printer, label: "Print unlimited" },
              { Icon: BadgeCheck, label: "Secure delivery" },
            ].map((b) => (
              <div key={b.label} className="rounded-2xl bg-white px-2 py-4 shadow-sm">
                <b.Icon className="mx-auto h-6 w-6 text-brand-blue" />
                <p className="mt-1.5 text-xs font-bold text-slate-500">{b.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {product.long_description && (
        <section className="mt-16 max-w-3xl">
          <h2 className="font-display text-3xl font-bold text-slate-800">About this workbook</h2>
          <p className="mt-4 text-lg font-semibold leading-relaxed text-slate-600">{product.long_description}</p>
        </section>
      )}

      <section className="mt-14 max-w-3xl">
        <h2 className="font-display text-3xl font-bold text-slate-800">Frequently asked questions</h2>
        <Accordion type="single" collapsible className="mt-5 space-y-3" data-testid="product-faq">
          {PRODUCT_FAQ.map((f, i) => (
            <AccordionItem key={i} value={`faq-${i}`} className="rounded-2xl border-0 bg-white px-6 shadow-md shadow-blue-900/5">
              <AccordionTrigger className="font-display text-lg font-bold text-slate-700 hover:no-underline">
                {f.q}
              </AccordionTrigger>
              <AccordionContent className="text-base font-semibold text-slate-500">{f.a}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </section>

      <CheckoutModal product={product} open={buyOpen} onClose={() => setBuyOpen(false)} />
      <PreviewModal product={product} open={previewOpen} onClose={() => setPreviewOpen(false)} />
    </div>
  );
}
